/**
 * MoneyField component input
 * Handles automatic formatting and conversion between Cents/Rappen and EUR/CHF
 */

import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  formatCHFInput,
  parseCHF,
  chfToRappen,
  rappenToCHF,
  DEFAULT_CURRENCY,
} from "@/lib/money";
import { useCurrency } from "@/hooks/useCurrency";
import type { FieldConfig } from "../types";

interface MoneyFieldProps<T> {
  field: FieldConfig<T>;
  value: number; // Value in Rappen (smallest unit)
  onChange: (value: number) => void; // Returns Rappen
  onBlur?: () => void;
  disabled?: boolean;
  id: string;
  placeholder?: string;
  "aria-describedby"?: string;
}

export function MoneyField<T>({
  field,
  value,
  onChange,
  onBlur,
  disabled,
  id,
  placeholder = "0.00",
  "aria-describedby": ariaDescribedBy,
}: MoneyFieldProps<T>) {
  const { currency } = useCurrency();
  const activeCurrency = currency || DEFAULT_CURRENCY;
  // Internal state for the formatted display value
  const [displayValue, setDisplayValue] = useState<string>("");
  const [isFocused, setIsFocused] = useState(false);

  // Update display value when prop value changes
  useEffect(() => {
    if (!isFocused) {
      // Convert from Rappen to CHF for display
      const chfValue = rappenToCHF(value || 0);
      setDisplayValue(formatCHFInput(chfValue, activeCurrency));
    }
  }, [value, isFocused, activeCurrency]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Allow typing of numbers, decimal point, and apostrophes. When the field
    // opts into negatives (allowNegative), also keep a single leading minus sign
    // so discounts/credits can be entered.
    const digits = inputValue.replace(/[^0-9.']/g, "");
    if (field.allowNegative && inputValue.trimStart().startsWith("-")) {
      setDisplayValue("-" + digits);
    } else {
      setDisplayValue(digits);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    // When focusing, show the raw number without thousand separators for easier editing
    const chfValue = rappenToCHF(value || 0);
    setDisplayValue(chfValue.toFixed(2));
  };

  const handleBlur = () => {
    setIsFocused(false);

    // Parse the input value and convert to Rappen
    const chfValue = parseCHF(displayValue, activeCurrency);
    const rappenValue = chfToRappen(chfValue);

    // Update the form value
    onChange(rappenValue);

    // Format the display value
    setDisplayValue(formatCHFInput(chfValue, activeCurrency));

    // Call the original onBlur if provided
    onBlur?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow Enter to submit the value
    if (e.key === "Enter") {
      handleBlur();
    }
  };

  return (
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
        {activeCurrency}
      </div>
      <Input
        id={id}
        type="text"
        inputMode="decimal"
        placeholder={placeholder}
        disabled={disabled}
        aria-describedby={ariaDescribedBy}
        aria-required={field.required || undefined}
        autoComplete={field.autoComplete}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="pl-12"
      />
    </div>
  );
}
